import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'motion/react';
import {
  ArrowRight,
  ArrowUpRight,
  Bot,
  Check,
  Lightbulb,
  Network,
  Smartphone,
  Sparkles,
  Workflow,
} from 'lucide-react';
import { AmbientCursor } from './components/AmbientCursor';
import { MagneticButton } from './components/MagneticButton';
import { Navigation, type Locale } from './components/Navigation';
import { SiteFooter } from './components/SiteFooter';
import { ParticleField } from './components/ParticleField';
import { HeroProjectShowcase } from './components/HeroProjectShowcase';
import {
  IdeaJourneyVisual,
  ProblemFlowVisual,
  ProductDevelopmentVisual,
  DigitizationVisual,
  AutomationVisual,
  FampoProductVisual,
  AssociationProductVisual,
} from './components/EditorialVisuals';
import { siteConfig } from './siteConfig';

const EASE = [0.22, 1, 0.36, 1] as const;

const copy = {
  sv: {
    metaTitle: 'Keevo Sverige – Appar, digitala system & AI-automation',
    metaDescription: 'Keevo är en svensk digital produktstudio som utvecklar appar, interna system och AI-automation – från idé eller verksamhetsproblem till fungerande produkt.',
    hero: {
      badge: 'Egna produkter · Utvalda projekt',
      location: 'Huvudkontor Sverige · Worldwide',
      line1: 'Från idé',
      line2: 'eller problem',
      line3: 'till något som',
      line4: 'fungerar.',
      body: 'Keevo analyserar, strukturerar och utvecklar digitala lösningar — från appar och interna system till AI och automation. Vi börjar där du är, bygger det som behövs och förbättrar längs vägen.',
      idea: 'Berätta om din idé',
      problem: 'Beskriv ett problem',
      areas: ['Produktutveckling', 'Digitalisering & system', 'AI & automation'],
    },
    paths: {
      eyebrow: 'Två sätt att börja',
      title: 'Du behöver inte komma med en färdig kravspecifikation.',
      intro: 'Ett projekt kan börja med en lös idé eller med ett arbetssätt som helt enkelt borde fungera bättre.',
      items: [
        {
          icon: Lightbulb,
          tag: '01 · IDÉ',
          title: 'Jag har en idé.',
          body: 'Du vet ungefär vad du vill skapa men inte hur du ska ta det vidare. Keevo hjälper dig att förstå idén, definiera vad som behöver byggas och göra den konkret.',
          cta: 'Berätta om idén',
          kind: 'idea' as const,
        },
        {
          icon: Workflow,
          tag: '02 · PROBLEM',
          title: 'Jag har ett problem.',
          body: 'En process tar för lång tid, information finns på flera ställen eller samma arbete görs manuellt om och om igen. Vi börjar med att förstå friktionen och bygger därifrån.',
          cta: 'Beskriv problemet',
          kind: 'problem' as const,
        },
      ],
    },
    services: {
      eyebrow: 'Vad Keevo gör',
      title: 'Problem först. Teknikval därefter.',
      intro: 'Tre kärnområden täcker vägen från första tanke till fungerande digital lösning.',
      items: [
        {
          icon: Smartphone,
          number: '01',
          title: 'Produktutveckling',
          body: 'Mobilappar, webbappar, prototyper och MVP:er. Från idé och struktur till UX/UI och fungerande produkt.',
          note: 'iOS · Android · Webb · MVP · UX/UI',
        },
        {
          icon: Network,
          number: '02',
          title: 'Digitalisering & system',
          body: 'Vi undersöker processer, informationsflöden och återkommande administration för att skapa enklare interna verktyg och digitala system.',
          note: 'Processer · Interna system · Digitalisering',
        },
        {
          icon: Bot,
          number: '03',
          title: 'AI & automation',
          body: 'AI och automation används där det faktiskt minskar manuellt arbete, strukturerar information eller möjliggör funktioner som tidigare varit svåra att genomföra.',
          note: 'AI-integration · Automation · Arbetsflöden',
        },
      ],
    },
    products: {
      eyebrow: 'Produkter vi bygger',
      title: 'Verkliga system. Under verklig utveckling.',
      intro: 'Keevos produkter visas med sin faktiska status — utan påhittade användarsiffror eller resultat.',
      fampo: {
        status: 'PRE-LAUNCH · ANDROID',
        title: 'FamPo',
        body: 'En fungerande familjeapp för uppgifter, poäng och belöningar. Säkerhet, funktionalitet och databasflöden färdigställs nu innan den sista större UI/UX-bearbetningen inför lansering.',
        result: 'Nära produktionsklar inför lansering',
        cta: 'Utforska FamPo',
      },
      association: {
        status: 'AKTIV UTVECKLING · PLATTFORM',
        title: 'Din Förening',
        body: 'Ett fungerande digitalt system under fortsatt utveckling, skapat för att samla och förenkla föreningsrelaterade administrativa flöden och kommunikation.',
        result: 'Fungerande system · aktiv utveckling',
      },
    },
    process: {
      eyebrow: 'Så arbetar Keevo',
      title: 'Förstå. Bygg. Testa. Förbättra.',
      intro: 'Analys ska leda till handling. Vi skapar en utförandeplan, gör lösningen konkret tidigt och låter verkliga lärdomar förbättra nästa steg.',
      items: [
        ['01', 'Förstå', 'Vi sätter oss in i problemet, människorna, processen och begränsningarna tills nästa steg går att definiera.'],
        ['02', 'Bygg', 'Vi gör idén konkret med prototyp, flöde eller fungerande produkt så tidigt som det är rimligt.'],
        ['03', 'Testa', 'Vi granskar hur lösningen fungerar i verkligheten — tekniskt, funktionellt och för människorna som ska använda den.'],
        ['04', 'Förbättra', 'Det vi lär oss används direkt för att förenkla, prioritera och utveckla nästa version.'],
      ],
    },
    story: {
      eyebrow: 'Varför Keevo finns',
      title: 'Färre processer som fastnar. Fler lösningar som går att prova.',
      p1: 'Keevo växte fram ur erfarenheter från service, industri och offentlig verksamhet — miljöer där verkliga problem ofta kräver både djup förståelse och praktiskt genomförande.',
      p2: 'Bra förbättringsidéer kan fastna mellan tidsbrist, komplexa system, gamla arbetssätt, lagkrav och långa teoretiska diskussioner. Samtidigt har moderna utvecklingsverktyg och AI gjort det möjligt att snabbare strukturera ett problem, testa ett koncept och lära sig av något konkret.',
      p3: 'Keevo skapades för att använda den möjligheten praktiskt. Effektivisering ska inte bara betyda att människor pressas att göra mer — bra system kan minska onödig friktion, frigöra tid och använda pengar och resurser bättre.',
      principle: 'Förstå tillräckligt för att börja. Förbättra med varje steg.',
    },
    founder: {
      eyebrow: 'Grundare',
      name: 'Kevan Issa',
      role: 'Grundare · System/App- och effektiviseringskonsult',
      body: 'Ett långvarigt intresse för problemlösning och digital utveckling, kombinerat med erfarenheter från flera typer av verksamheter, ligger bakom Keevos arbetssätt: förstå helheten, göra nästa steg konkret och bygga lösningar som fungerar utanför presentationsrummet.',
      detail: 'Keevo är svenskt i grunden och arbetar mot både svenska och internationella idéer, produkter och samarbeten.',
    },
    contact: {
      eyebrow: 'Nästa steg',
      title: 'Har du en idé — eller ett problem som borde gå att lösa bättre?',
      body: 'Du behöver inte veta vilken teknik som behövs. Börja med att beskriva vad du vill skapa eller vad som inte fungerar idag.',
      idea: 'Berätta om din idé',
      problem: 'Beskriv problemet',
      direct: 'Eller mejla direkt',
      checks: ['Ingen färdig kravspecifikation krävs', 'Vi börjar där du är', 'Svenska eller engelska'],
    },
    footer: {
      tagline: 'Digitala produkter · System · AI & automation',
      location: 'Huvudkontor Sverige',
      sole: 'Enskild firma',
      org: 'Org.nr',
      contact: 'Kontakt',
    },
  },
  en: {
    metaTitle: 'Keevo Sweden – Apps, Digital Systems & AI Automation',
    metaDescription: 'Keevo is a Swedish digital product studio building apps, internal systems and AI automation – from idea or operational problem to a working product.',
    hero: {
      badge: 'Own products · Selected projects',
      location: 'Headquartered in Sweden · Worldwide',
      line1: 'From idea',
      line2: 'or problem',
      line3: 'to something that',
      line4: 'works.',
      body: 'Keevo turns ideas, inefficient processes and real-world challenges into practical digital solutions — from apps and internal systems to AI and automation. We start where you are, build what is needed and improve along the way.',
      idea: 'Tell us your idea',
      problem: 'Describe a problem',
      areas: ['Product development', 'Digitization & systems', 'AI & automation'],
    },
    paths: {
      eyebrow: 'Two ways to start',
      title: 'You do not need a finished technical specification.',
      intro: 'A project can start with a rough idea or with a way of working that simply should work better.',
      items: [
        {
          icon: Lightbulb,
          tag: '01 · IDEA',
          title: 'I have an idea.',
          body: 'You know roughly what you want to create but not how to move forward. Keevo helps clarify the idea, define what needs to be built and make it concrete.',
          cta: 'Tell us the idea',
          kind: 'idea' as const,
        },
        {
          icon: Workflow,
          tag: '02 · PROBLEM',
          title: 'I have a problem.',
          body: 'A process takes too long, information lives in several places or the same work is repeated manually. We start by understanding the friction and build from there.',
          cta: 'Describe the problem',
          kind: 'problem' as const,
        },
      ],
    },
    services: {
      eyebrow: 'What Keevo does',
      title: 'Problem first. Technology choice second.',
      intro: 'Three core areas cover the path from the first thought to a working digital solution.',
      items: [
        {
          icon: Smartphone,
          number: '01',
          title: 'Product development',
          body: 'Mobile apps, web apps, prototypes and MVPs. From idea and structure to UX/UI and a working product.',
          note: 'iOS · Android · Web · MVP · UX/UI',
        },
        {
          icon: Network,
          number: '02',
          title: 'Digitization & systems',
          body: 'We examine processes, information flows and recurring administration to create simpler internal tools and digital systems.',
          note: 'Processes · Internal systems · Digitization',
        },
        {
          icon: Bot,
          number: '03',
          title: 'AI & automation',
          body: 'AI and automation are used where they genuinely reduce manual work, structure information or enable functions that were previously difficult to implement.',
          note: 'AI integration · Automation · Workflows',
        },
      ],
    },
    products: {
      eyebrow: 'Products we build',
      title: 'Real systems. Under real development.',
      intro: 'Keevo products are shown with their actual status — without invented user numbers or results.',
      fampo: {
        status: 'PRE-LAUNCH · ANDROID',
        title: 'FamPo',
        body: 'A working family app for chores, points and rewards. Security, functionality and database flows are now being finalized before the last major UI/UX refinement ahead of launch.',
        result: 'Close to production-ready before launch',
        cta: 'Explore FamPo',
      },
      association: {
        status: 'ACTIVE DEVELOPMENT · PLATFORM',
        title: 'Din Förening',
        body: 'A working digital system in continued development, created to bring together and simplify association-related administration and communication flows.',
        result: 'Working system · active development',
      },
    },
    process: {
      eyebrow: 'How Keevo works',
      title: 'Understand. Build. Test. Improve.',
      intro: 'Analysis should lead to action. We form an execution plan, make the solution concrete early and let real learning improve the next step.',
      items: [
        ['01', 'Understand', 'We learn the problem, people, process and constraints until the next step can be defined.'],
        ['02', 'Build', 'We make the idea concrete with a prototype, workflow or working product as early as reasonably possible.'],
        ['03', 'Test', 'We examine how the solution works in reality — technically, functionally and for the people who will use it.'],
        ['04', 'Improve', 'What we learn is used directly to simplify, prioritize and develop the next version.'],
      ],
    },
    story: {
      eyebrow: 'Why Keevo exists',
      title: 'Fewer processes that get stuck. More solutions that can be tried.',
      p1: 'Keevo grew out of experience across service, industry and the public sector — environments where real problems often require both deep understanding and practical execution.',
      p2: 'Good improvement ideas can become stuck between limited time, complex systems, legacy ways of working, regulatory requirements and long theoretical discussions. At the same time, modern development tools and AI have made it possible to structure a problem, test a concept and learn from something concrete much faster.',
      p3: 'Keevo was created to use that opportunity in practice. Efficiency should not simply mean pushing people to do more — good systems can remove unnecessary friction, free up time and use money and resources better.',
      principle: 'Understand enough to begin. Improve with every step.',
    },
    founder: {
      eyebrow: 'Founder',
      name: 'Kevan Issa',
      role: 'Founder · Systems, App & Process Improvement Consultant',
      body: 'A long-standing interest in problem-solving and digital development, combined with experience across different kinds of organizations, shapes Keevo’s approach: understand the whole, make the next step concrete and build solutions that work outside the presentation room.',
      detail: 'Keevo is Swedish at its core and works toward both Swedish and international ideas, products and partnerships.',
    },
    contact: {
      eyebrow: 'Next step',
      title: 'Have an idea — or a problem that should work better?',
      body: 'You do not need to know which technology is required. Start by describing what you want to create or what is not working today.',
      idea: 'Tell us your idea',
      problem: 'Describe the problem',
      direct: 'Or email directly',
      checks: ['No finished specification required', 'We start where you are', 'Swedish or English'],
    },
    footer: {
      tagline: 'Digital products · Systems · AI & automation',
      location: 'Headquartered in Sweden',
      sole: 'Sole proprietorship',
      org: 'Org. no.',
      contact: 'Contact',
    },
  },
} as const;

export default function KeevoWebsite() {
  const [locale] = useState<Locale>(() => window.location.pathname.startsWith('/en') ? 'en' : 'sv');
  const shouldReduceMotion = useReducedMotion();
  const t = copy[locale];
  const heroRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll();
  const progressScaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroTextY = useTransform(heroProgress, [0, 1], [0, 52]);
  const heroVisualY = useTransform(heroProgress, [0, 1], [0, 78]);
  const heroVisualRotate = useTransform(heroProgress, [0, 1], [0, 4]);

  useEffect(() => {
    document.documentElement.lang = locale === 'sv' ? 'sv-SE' : 'en';
    document.title = t.metaTitle;
    const description = document.querySelector('meta[name="description"]');
    if (description) description.setAttribute('content', t.metaDescription);
  }, [locale, t.metaDescription, t.metaTitle]);

  const changeLocale = (nextLocale: Locale) => {
    if (nextLocale === locale) return;
    const nextPath = nextLocale === 'en' ? '/en/' : '/';
    window.location.assign(`${nextPath}${window.location.hash}`);
  };


  const openContact = (kind: 'idea' | 'problem') => {
    const subject = locale === 'sv'
      ? (kind === 'idea' ? 'Jag vill berätta om en idé' : 'Jag vill beskriva ett problem')
      : (kind === 'idea' ? 'I would like to tell you about an idea' : 'I would like to describe a problem');
    window.location.href = `mailto:${siteConfig.email}?subject=${encodeURIComponent(subject)}`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: shouldReduceMotion ? 0 : 0.07, delayChildren: shouldReduceMotion ? 0 : 0.08 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: shouldReduceMotion ? 0 : 0.72, ease: EASE } },
  };

  return (
    <div className="min-h-screen bg-[#F4F3EF] overflow-x-hidden text-[#12141A]">
      <div className="grain-overlay" aria-hidden />
      <AmbientCursor />
      <motion.div className="fixed top-0 left-0 right-0 h-[3px] z-[60] origin-left" style={{ scaleX: progressScaleX, background: 'linear-gradient(90deg,#5B4BFF 0%,#7C6DFF 60%,#AFA5FF 100%)' }} aria-hidden />
      <Navigation locale={locale} onLocaleChange={changeLocale} />

      <main>
        <section id="hem" ref={heroRef} className="keevo-hero relative min-h-screen flex items-center px-6 md:px-8 lg:px-16 pt-36 pb-24 lg:pt-32 overflow-hidden isolation-isolate">
          <div className="keevo-light-grid" aria-hidden />
          <div className="keevo-aurora keevo-aurora--one" aria-hidden />
          <div className="keevo-aurora keevo-aurora--two" aria-hidden />
          <ParticleField />

          <div className="relative z-10 max-w-[1600px] mx-auto w-full">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 xl:gap-14 items-center">
              <motion.div
                className="lg:col-span-6 xl:col-span-7 space-y-8"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                style={shouldReduceMotion ? undefined : { y: heroTextY }}
              >
                <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-3">
                  <div className="inline-flex items-center gap-2.5 pl-3 pr-4 py-2 bg-white/75 backdrop-blur-md border border-[#E2DFEA] rounded-full shadow-[0_8px_30px_rgba(45,38,86,0.035)]">
                    <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-[#7C6DFF] opacity-30 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#5B4BFF]" /></span>
                    <span className="text-[12px] text-[#565A63] tracking-[0.04em] font-mono font-medium">{t.hero.badge}</span>
                  </div>
                  <span className="text-[12px] text-[#71757E] font-mono">{t.hero.location}</span>
                </motion.div>

                <motion.h1 variants={itemVariants} className="font-sora font-extrabold text-[50px] sm:text-[61px] md:text-[72px] lg:text-[74px] xl:text-[86px] leading-[0.98] tracking-[-0.055em] max-w-[900px]">
                  {t.hero.line1}<br />
                  <span className="bg-gradient-to-r from-[#5B4BFF] to-[#7C6DFF] bg-clip-text text-transparent">{t.hero.line2}</span><br />
                  <span className="text-[#12141A]">{t.hero.line3}</span><br />
                  <span className="bg-gradient-to-r from-[#5B4BFF] to-[#4B37E0] bg-clip-text text-transparent">{t.hero.line4}</span>
                </motion.h1>

                <motion.p variants={itemVariants} className="text-[18px] text-[#565A63] leading-[1.7] max-w-[650px] font-manrope">
                  {t.hero.body}
                </motion.p>

                <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 items-start">
                  <MagneticButton onClick={() => openContact('idea')} className="px-9 py-[18px] rounded-full text-[16px] inline-flex items-center gap-2" variant="primary">
                    {t.hero.idea}<ArrowRight size={18} />
                  </MagneticButton>
                  <MagneticButton onClick={() => openContact('problem')} className="px-9 py-[18px] rounded-full text-[16px] inline-flex items-center gap-2" variant="secondary">
                    {t.hero.problem}<ArrowUpRight size={18} />
                  </MagneticButton>
                </motion.div>


              </motion.div>

              <motion.div className="lg:col-span-6 xl:col-span-5 flex justify-center lg:justify-end relative min-h-[470px] lg:min-h-[620px] items-center" style={shouldReduceMotion ? undefined : { y: heroVisualY, rotate: heroVisualRotate }}>
                <div className="signature-glow" aria-hidden />
                <motion.div className="w-full max-w-[600px]" initial={{ opacity: 0, scale: 0.94, y: 26 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 1.05, delay: 0.25, ease: EASE }}>
                  <HeroProjectShowcase
                    locale={locale}
                    onOpenFampo={() => { window.location.href = locale === 'en' ? '/en/fampo/' : '/fampo/'; }}
                    onOpenAssociation={() => document.getElementById('arbete')?.scrollIntoView({ behavior: 'smooth' })}
                  />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="section-soft relative px-6 md:px-8 lg:px-16 py-28 lg:py-36">
          <div className="max-w-[1350px] mx-auto">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}>
              <motion.div variants={itemVariants} className="section-eyebrow"><span />{t.paths.eyebrow}</motion.div>
              <motion.h2 variants={itemVariants} className="section-title max-w-[1050px]">{t.paths.title}</motion.h2>
              <motion.p variants={itemVariants} className="section-intro max-w-[700px]">{t.paths.intro}</motion.p>

              <div className="path-showcases mt-14">
                {t.paths.items.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.article key={item.tag} variants={itemVariants} className={`path-showcase ${index === 1 ? 'path-showcase--reverse' : ''}`}>
                      <div className="path-showcase__copy">
                        <div className="path-showcase__meta"><span>{item.tag}</span><div className="path-card__icon"><Icon size={22} /></div></div>
                        <h3>{item.title}</h3>
                        <p>{item.body}</p>
                        <button onClick={() => openContact(item.kind)} className="path-card__link">{item.cta}<ArrowRight size={17} /></button>
                      </div>
                      <div className="path-showcase__visual">
                        {index === 0 ? <IdeaJourneyVisual locale={locale} /> : <ProblemFlowVisual locale={locale} />}
                      </div>
                    </motion.article>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>

        <section id="tjanster" className="relative py-28 lg:py-36 px-6 md:px-8 lg:px-16 bg-[#F4F3EF]">
          <div className="section-aurora section-aurora--right" aria-hidden />
          <div className="max-w-[1350px] mx-auto relative z-10">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}>
              <motion.div variants={itemVariants} className="section-eyebrow"><span />{t.services.eyebrow}</motion.div>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                <motion.h2 variants={itemVariants} className="section-title lg:col-span-8">{t.services.title}</motion.h2>
                <motion.p variants={itemVariants} className="section-intro lg:col-span-4 lg:pb-2">{t.services.intro}</motion.p>
              </div>

              <div className="service-stories mt-14">
                {t.services.items.map((item, index) => {
                  const Icon = item.icon;
                  const Visual = index === 0 ? ProductDevelopmentVisual : index === 1 ? DigitizationVisual : AutomationVisual;
                  const serviceHref = locale === 'sv'
                    ? ['/apputveckling/', '/digitalisering/', '/ai-automation/'][index]
                    : ['/en/app-development/', '/en/digitization/', '/en/ai-automation/'][index];
                  return (
                    <motion.article key={item.number} variants={itemVariants} className={`service-story service-story--${index + 1}`}>
                      <div className="service-story__copy">
                        <div className="service-story__top"><span className="service-card__number">{item.number}</span><div className="service-card__icon"><Icon size={23} /></div></div>
                        <h3>{item.title}</h3>
                        <p>{item.body}</p>
                        <div className="service-card__note">{item.note}</div>
                        <a href={serviceHref} className="path-card__link">
                          {locale === 'sv' ? 'Läs mer' : 'Learn more'}<ArrowRight size={17} />
                        </a>
                      </div>
                      <div className="service-story__visual"><Visual locale={locale} /></div>
                    </motion.article>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>

        <section id="arbete" className="relative py-28 lg:py-36 px-6 md:px-8 lg:px-16 bg-[#FAF9F6] overflow-hidden">
          <div className="max-w-[1450px] mx-auto">
            <motion.div className="max-w-[900px]" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}>
              <motion.div variants={itemVariants} className="section-eyebrow"><span />{t.products.eyebrow}</motion.div>
              <motion.h2 variants={itemVariants} className="section-title">{t.products.title}</motion.h2>
              <motion.p variants={itemVariants} className="section-intro max-w-[720px]">{t.products.intro}</motion.p>
            </motion.div>

            <div className="space-y-14 lg:space-y-20 mt-14">
              <motion.article className="product-row" initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.8, ease: EASE }}>
                <div className="product-row__visual"><FampoProductVisual locale={locale} /></div>
                <div className="product-row__copy">
                  <span className="product-status">{t.products.fampo.status}</span>
                  <h3>{t.products.fampo.title}</h3>
                  <p>{t.products.fampo.body}</p>
                  <div className="product-result"><Sparkles size={16} />{t.products.fampo.result}</div>
                  <button onClick={() => { window.location.href = locale === 'en' ? '/en/fampo/' : '/fampo/'; }} className="product-link">{t.products.fampo.cta}<ArrowUpRight size={18} /></button>
                </div>
              </motion.article>

              <motion.article className="product-row product-row--reverse" initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.8, ease: EASE }}>
                <div className="product-row__visual"><AssociationProductVisual locale={locale} /></div>
                <div className="product-row__copy">
                  <span className="product-status">{t.products.association.status}</span>
                  <h3>{t.products.association.title}</h3>
                  <p>{t.products.association.body}</p>
                  <div className="product-result"><Sparkles size={16} />{t.products.association.result}</div>
                </div>
              </motion.article>
            </div>
          </div>
        </section>

        <section id="om" className="about-flow relative py-28 lg:py-40 px-6 md:px-8 lg:px-16 overflow-hidden">
          <div className="about-flow__grid" aria-hidden />
          <div className="about-flow__aurora" aria-hidden />
          <div className="max-w-[1450px] mx-auto relative z-10">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }}>
              <motion.div variants={itemVariants} className="section-eyebrow"><span />{t.process.eyebrow}</motion.div>
              <motion.h2 variants={itemVariants} className="section-title max-w-[1100px]">{t.process.title}</motion.h2>
              <motion.p variants={itemVariants} className="section-intro max-w-[760px]">{t.process.intro}</motion.p>

              <div className="process-grid mt-16">
                {t.process.items.map(([number, title, body], index) => (
                  <motion.div key={number} variants={itemVariants} className="process-step">
                    <div className="process-step__top"><span>{number}</span><i /></div>
                    <h3>{title}</h3>
                    <p>{body}</p>
                    {index < t.process.items.length - 1 && <ArrowRight className="process-step__arrow" size={18} />}
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <div className="story-grid mt-28 lg:mt-36">
              <motion.div initial={{ opacity: 0, y: 35 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.75, ease: EASE }}>
                <div className="section-eyebrow"><span />{t.story.eyebrow}</div>
                <h2 className="story-title">{t.story.title}</h2>
              </motion.div>
              <motion.div className="story-copy" initial={{ opacity: 0, y: 35 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.75, delay: 0.08, ease: EASE }}>
                <p>{t.story.p1}</p><p>{t.story.p2}</p><p>{t.story.p3}</p>
                <blockquote>{t.story.principle}</blockquote>
              </motion.div>
            </div>

            <div className="founder-grid mt-28 lg:mt-36">
              <motion.div className="founder-photo" initial={{ opacity: 0, x: -35 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}>
                <img src="/kevan-issa-founder-editorial.png" alt="Kevan Issa" />
                <div className="founder-photo__glow" aria-hidden />
              </motion.div>
              <motion.div className="founder-copy" initial={{ opacity: 0, x: 35 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}>
                <div className="section-eyebrow"><span />{t.founder.eyebrow}</div>
                <h2>{t.founder.name}</h2>
                <strong>{t.founder.role}</strong>
                <p>{t.founder.body}</p>
                <p className="founder-copy__detail">{t.founder.detail}</p>
                <a href={locale === 'sv' ? '/om-keevo/' : '/en/about/'} className="path-card__link">
                  {locale === 'sv' ? 'Läs mer om Keevo' : 'Learn more about Keevo'}<ArrowRight size={17} />
                </a>
              </motion.div>
            </div>
          </div>
        </section>

        <section id="kontakt" className="contact-light relative py-28 lg:py-40 px-6 md:px-8 lg:px-16 overflow-hidden">
          <div className="contact-light__grid" aria-hidden />
          <div className="contact-light__aurora contact-light__aurora--one" aria-hidden />
          <div className="contact-light__aurora contact-light__aurora--two" aria-hidden />
          <motion.div className="max-w-[1100px] mx-auto text-center relative z-10" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }}>
            <motion.div variants={itemVariants} className="section-eyebrow justify-center"><span />{t.contact.eyebrow}</motion.div>
            <motion.h2 variants={itemVariants} className="font-sora font-bold text-[42px] md:text-[62px] lg:text-[76px] leading-[1.04] tracking-[-0.05em] text-[#12141A]">{t.contact.title}</motion.h2>
            <motion.p variants={itemVariants} className="mx-auto mt-7 text-[18px] text-[#565A63] leading-[1.75] max-w-[700px] font-manrope">{t.contact.body}</motion.p>
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
              <MagneticButton onClick={() => openContact('idea')} className="px-9 py-[18px] rounded-full text-[16px] inline-flex items-center justify-center gap-2" variant="primary">{t.contact.idea}<ArrowRight size={18} /></MagneticButton>
              <MagneticButton onClick={() => openContact('problem')} className="px-9 py-[18px] rounded-full text-[16px] inline-flex items-center justify-center gap-2" variant="secondary">{t.contact.problem}<ArrowUpRight size={18} /></MagneticButton>
            </motion.div>
            <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-x-7 gap-y-3 mt-10">
              {t.contact.checks.map((item) => <span key={item} className="inline-flex items-center gap-2 text-[14px] text-[#63656D] font-manrope"><Check size={16} className="text-[#5B4BFF]" />{item}</span>)}
            </motion.div>
            <motion.div variants={itemVariants} className="mt-8 text-[15px] text-[#71757E] font-manrope">{t.contact.direct} <a href={`mailto:${siteConfig.email}`} className="text-[#5B4BFF] hover:text-[#4B37E0] font-semibold">{siteConfig.email}</a></motion.div>
          </motion.div>
        </section>
      </main>

      <SiteFooter locale={locale} />
    </div>
  );
}
